/**
 * ページネーション（番号付き / ロードモア）
 */

export function setupPagination( block, config ) {
	const pagination = block.dataset.pagination;

	if ( pagination === 'numbered' ) {
		setupNumbered( block, config );
	} else if ( pagination === 'loadmore' ) {
		setupLoadMore( block, config );
	}
}

/* ------------------------------------------------------------------ */
/*  番号付きページネーション                                            */
/* ------------------------------------------------------------------ */

function setupNumbered( block, config ) {
	block.addEventListener( 'click', ( e ) => {
		const btn = e.target.closest( '.sab-yt-page-btn' );
		if ( ! btn || btn.disabled ) return;

		const page = parseInt( btn.dataset.page, 10 );
		if ( isNaN( page ) ) return;

		fetchPage( block, config, page ).then( ( data ) => {
			if ( ! data ) return;
			replaceGrid( block, data.videos, config );
			updateNumberedPagination( block, page, config.totalPages );
			config.page = page;
			config.log( `PAGINATION navigate to page=${ page }` );
			scrollToBlock( block, config.scrollOffset || 0 );
		} );
	} );
}

/**
 * ページネーションボタンを畳み込み形式で再生成する
 * 形式: ‹ 1 … 4 [5] 6 … 10 ›
 */
function updateNumberedPagination( block, currentPage, totalPages ) {
	const nav = block.querySelector( '.sab-yt-pagination' );
	if ( ! nav ) return;

	nav.innerHTML = buildPaginationHTML( currentPage, totalPages );
}

function buildPaginationHTML( current, total ) {
	const window_ = 2; // 現在ページの前後に表示するページ数
	const pages   = [];
	let   html    = '';

	// 表示するページ番号を収集
	for ( let i = 1; i <= total; i++ ) {
		if ( i === 1 || i === total || ( i >= current - window_ && i <= current + window_ ) ) {
			pages.push( i );
		}
	}

	// 前へボタン
	html += `<button class="sab-yt-page-btn sab-yt-page-prev" data-page="${ Math.max( 1, current - 1 ) }" aria-label="前のページ"${ current <= 1 ? ' disabled' : '' }>‹</button>`;

	// ページ番号ボタン
	let prevPage = 0;
	for ( const p of pages ) {
		if ( prevPage && p - prevPage > 1 ) {
			html += `<span class="sab-yt-page-ellipsis" aria-hidden="true">…</span>`;
		}
		const isCurrent = p === current;
		html += `<button class="sab-yt-page-btn${ isCurrent ? ' is-current' : '' }" data-page="${ p }"${ isCurrent ? ' aria-current="page"' : '' }>${ p }</button>`;
		prevPage = p;
	}

	// 次へボタン
	html += `<button class="sab-yt-page-btn sab-yt-page-next" data-page="${ Math.min( total, current + 1 ) }" aria-label="次のページ"${ current >= total ? ' disabled' : '' }>›</button>`;

	return html;
}

/* ------------------------------------------------------------------ */
/*  ロードモア                                                          */
/* ------------------------------------------------------------------ */

function setupLoadMore( block, config ) {
	block.addEventListener( 'click', ( e ) => {
		const btn = e.target.closest( '.sab-yt-loadmore-btn' );
		if ( ! btn ) return;

		const page = parseInt( btn.dataset.page, 10 );
		if ( isNaN( page ) ) return;

		btn.disabled = true;
		btn.textContent = '読み込み中…';

		fetchPage( block, config, page ).then( ( data ) => {
			if ( ! data ) {
				btn.disabled = false;
				btn.textContent = 'さらに読み込む';
				return;
			}

			appendCards( block, data.videos, config );
			config.log( `LOADMORE appended ${ data.videos.length } items (page=${ page })`, { has_more: data.has_more } );

			const wrap = block.querySelector( '.sab-yt-loadmore-wrap' );
			if ( data.has_more ) {
				btn.disabled = false;
				btn.textContent = 'さらに読み込む';
				btn.dataset.page = page + 1;
			} else if ( wrap ) {
				wrap.remove();
			}
		} );
	} );
}

/* ------------------------------------------------------------------ */
/*  スクロールユーティリティ                                            */
/* ------------------------------------------------------------------ */

/**
 * ブロック上端へスクロールする。
 * offset（px）だけ上方向にずらすことで追従ヘッダーの裏に隠れるのを防ぐ。
 */
function scrollToBlock( block, offset ) {
	const top = block.getBoundingClientRect().top + window.scrollY - offset;
	window.scrollTo( { top: Math.max( 0, top ), behavior: 'smooth' } );
}

/* ------------------------------------------------------------------ */
/*  共通：API フェッチ                                                  */
/* ------------------------------------------------------------------ */

async function fetchPage( block, config, page ) {
	const { attrs, restUrl, nonce, log } = config;

	const params = new URLSearchParams( {
		mode:     attrs.mode,
		sources:  JSON.stringify( attrs.sources ),
		filters:  JSON.stringify( attrs.filters || [] ),
		order:    attrs.order,
		dedup:    attrs.dedup ? '1' : '0',
		perPage:  attrs.perPage,
		maxItems: attrs.maxItems || 0,
		page,
	} );

	const fetchTime = new Date().toLocaleTimeString( 'ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' } );
	log( `[${ fetchTime }] FETCH page=${ page }`, { url: `${ restUrl }?${ params }` } );
	const t0 = performance.now();

	try {
		const res = await fetch( `${ restUrl }?${ params }`, {
			headers: { 'X-WP-Nonce': nonce },
		} );
		if ( ! res.ok ) throw new Error( `HTTP ${ res.status }` );
		const data = await res.json();
		const elapsed  = Math.round( performance.now() - t0 );
		const respTime = new Date().toLocaleTimeString( 'ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' } );

		const debugInfo = data._debug || {};
		log( `[${ respTime }] RESPONSE page=${ page } (${ elapsed }ms)`, {
			...debugInfo,
			total:      data.total,
			returned:   data.videos?.length,
			has_more:   data.has_more,
			elapsed_ms: elapsed,
		} );

		// キャッシュステータスを目立つ形で出力
		if ( debugInfo.cache_status ) {
			const cacheMessages = {
				hit:         '✅ CACHE HIT — キャッシュから表示（APIコールなし）',
				fresh:       '🔄 FRESH FETCH — APIから新規取得',
				stale_quota: '⚠️ STALE (QUOTA) — クォータ枯渇のため古いキャッシュから表示',
				stale_error: '⚠️ STALE (API ERROR) — APIエラーのため古いキャッシュから表示',
				stale_lock:  '⚠️ STALE (LOCK WAIT) — 並行リクエストのため古いキャッシュから表示',
			};
			const msg = cacheMessages[ debugInfo.cache_status ] || `❓ ${ debugInfo.cache_status }`;
			const isStale = debugInfo.cache_status.startsWith( 'stale' );
			const statusTime = new Date().toLocaleTimeString( 'ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' } );
			if ( isStale ) {
				console.warn( `[SAB YT] [${ statusTime }] page=${ page } ${ msg }` );
			} else {
				console.log( `%c[SAB YT] [${ statusTime }] page=${ page } ${ msg }`, 'color:#00a32a;font-weight:bold;' );
			}
		}
		return data;
	} catch ( err ) {
		console.error( '[SAB YT] fetch error:', err );
		log( `FETCH ERROR page=${ page }`, { error: err.message } );
		return null;
	}
}

/* ------------------------------------------------------------------ */
/*  DOM 操作                                                            */
/* ------------------------------------------------------------------ */

function replaceGrid( block, videos, config ) {
	const grid = block.querySelector( '.sab-yt-grid' );
	if ( ! grid ) return;
	grid.innerHTML = '';
	videos.forEach( ( v ) => grid.appendChild( buildCard( v, config ) ) );
}

function appendCards( block, videos, config ) {
	const grid = block.querySelector( '.sab-yt-grid' );
	if ( ! grid ) return;
	videos.forEach( ( v ) => grid.appendChild( buildCard( v, config ) ) );
}

function buildCard( video, config ) {
	const attrs       = config.attrs || {};
	const playMode    = attrs.playMode    || 'modal';
	const showTitle   = attrs.showTitle   !== false;
	const showDesc    = attrs.showDescription === true;
	const showDate    = attrs.showDate    !== false;
	const showLabel   = attrs.showLabel   !== false;
	const multiSource = ( attrs.sources || [] ).length > 1;

	const article = document.createElement( 'article' );
	article.className       = 'sab-yt-card';
	article.dataset.videoId = video.videoId;

	const href   = playMode === 'newtab' ? `https://www.youtube.com/watch?v=${ video.videoId }` : '#';
	const target = playMode === 'newtab' ? ' target="_blank" rel="noopener noreferrer"' : '';

	const dateStr = video.publishedAt
		? new Date( video.publishedAt ).toLocaleDateString( 'ja-JP' )
		: '';

	const descText = video.description
		? ( video.description.length > 100 ? video.description.slice( 0, 100 ) + '…' : video.description )
		: '';

	article.innerHTML = `
		<a href="${ escHtml( href ) }" class="sab-yt-card__thumb-link" data-play-mode="${ escHtml( playMode ) }"${ target } aria-label="${ escHtml( video.title ) }">
			${ video.thumbnail
				? `<img src="${ escHtml( video.thumbnail ) }" alt="${ escHtml( video.title ) }" class="sab-yt-card__thumb" loading="lazy" />`
				: `<div class="sab-yt-card__thumb sab-yt-card__thumb--placeholder"></div>`
			}
			<span class="sab-yt-card__play-icon" aria-hidden="true"></span>
		</a>
		<div class="sab-yt-card__body">
			${ showTitle ? `<h3 class="sab-yt-card__title">${ escHtml( video.title ) }</h3>` : '' }
			${ showDesc && descText ? `<p class="sab-yt-card__desc">${ escHtml( descText ) }</p>` : '' }
			<div class="sab-yt-card__meta">
				${ showDate && dateStr ? `<time class="sab-yt-card__date" datetime="${ escHtml( video.publishedAt ) }">${ escHtml( dateStr ) }</time>` : '' }
				${ showLabel && multiSource && video._label ? `<span class="sab-yt-card__label">${ escHtml( video._label ) }</span>` : '' }
			</div>
		</div>
	`;

	return article;
}

function escHtml( str ) {
	return String( str )
		.replace( /&/g,  '&amp;'  )
		.replace( /</g,  '&lt;'   )
		.replace( />/g,  '&gt;'   )
		.replace( /"/g,  '&quot;' )
		.replace( /'/g,  '&#039;' );
}